/**
 * Offline-safe worker mutation queue (IndexedDB).
 *
 * Mirrors the Android offline punch design: an action the worker took is
 * recorded locally with its captured coordinates and a generated idempotency
 * key, kept VISIBLE until the backend acknowledges it, and replayed through
 * the existing worker APIs when connectivity returns.
 *
 * Two rules are absolute:
 *  - The queue is a convenience cache, never the authoritative record. Safari
 *    and iOS evict storage; a lost queue must degrade to "not recorded", never
 *    to a fabricated success.
 *  - The client never invents server state. A queued punch is reported as
 *    pending until the backend returns an acknowledgement, and an offline
 *    punch stays "requires review" because the backend routes it to supervisor
 *    review exactly as it does for Android.
 */

export type QueuedMutationKind = 'punch' | 'attendance_report' | 'on_the_way'

export type QueuedPunchAction = 'punch-in' | 'punch-out' | 'break-start' | 'break-end'

/** Client-generated failure reasons, localized where they are displayed. */
export type QueuedMutationFailureCode =
  | 'expired_beyond_sync_window'
  | 'missing_location'

export type QueuedMutationStatus =
  | 'pending'
  | 'syncing'
  | 'synced'
  | 'requires_review'
  | 'failed'

export interface QueuedMutation {
  /** Also the idempotency key replayed to the backend. */
  id: string
  kind: QueuedMutationKind
  action: string
  assignmentId: string
  locationId?: string | null
  /** Wall-clock moment the worker took the action, ISO-8601. */
  capturedAt: string
  latitude?: number | null
  longitude?: number | null
  accuracyMeters?: number | null
  notes?: string | null
  deviceContext: {
    userAgent?: string | null
    displayMode?: string | null
    networkType?: string | null
    appVersion?: string | null
  }
  status: QueuedMutationStatus
  attempts: number
  /**
   * Stable code for a failure this client generated. Codes are localized at
   * render time; `lastError` carries a backend-authored message verbatim and is
   * only used when there is no code.
   */
  failureCode?: QueuedMutationFailureCode | null
  lastError?: string | null
  serverEventId?: string | null
  createdAt: string
  updatedAt: string
}

export const DB_NAME = 'punch-worker-offline'
export const STORE_NAME = 'worker-mutations'
const DB_VERSION = 1

export function indexedDbSupported(): boolean {
  return typeof indexedDB !== 'undefined'
}

let dbPromise: Promise<IDBDatabase> | null = null

function openDatabase(): Promise<IDBDatabase> {
  if (!indexedDbSupported()) return Promise.reject(new Error('IndexedDB is unavailable in this browser.'))
  if (dbPromise) return dbPromise
  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)
    request.onupgradeneeded = () => {
      const db = request.result
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' })
        store.createIndex('status', 'status', { unique: false })
        store.createIndex('createdAt', 'createdAt', { unique: false })
      }
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error || new Error('Unable to open the offline queue.'))
  }).catch((error) => {
    dbPromise = null
    throw error
  })
  return dbPromise
}

async function withStore<T>(mode: IDBTransactionMode, run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await openDatabase()
  return new Promise<T>((resolve, reject) => {
    const transaction = db.transaction(STORE_NAME, mode)
    const request = run(transaction.objectStore(STORE_NAME))
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error || new Error('Offline queue operation failed.'))
  })
}

/** Idempotency key shared with the backend replay. */
export function createMutationId(prefix: string): string {
  const random = typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
  return `${prefix}:${random}`
}

export function readDeviceContext(): QueuedMutation['deviceContext'] {
  if (typeof navigator === 'undefined') return {}
  const connection = (navigator as any).connection
  const standalone = typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(display-mode: standalone)').matches
  return {
    userAgent: navigator.userAgent?.slice(0, 160) || null,
    displayMode: standalone ? 'standalone' : 'browser',
    networkType: connection?.effectiveType || null,
    appVersion: 'web',
  }
}

export async function enqueueMutation(input: Omit<QueuedMutation, 'status' | 'attempts' | 'createdAt' | 'updatedAt'>): Promise<QueuedMutation> {
  const now = new Date().toISOString()
  const record: QueuedMutation = { ...input, status: 'pending', attempts: 0, createdAt: now, updatedAt: now }
  await withStore('readwrite', (store) => store.put(record) as IDBRequest<any>)
  return record
}

export async function listMutations(): Promise<QueuedMutation[]> {
  if (!indexedDbSupported()) return []
  try {
    const rows = await withStore<QueuedMutation[]>('readonly', (store) => store.getAll() as IDBRequest<QueuedMutation[]>)
    return rows.sort((left, right) => left.createdAt.localeCompare(right.createdAt))
  } catch {
    // A blocked or evicted store must not break the worker screen.
    return []
  }
}

/** Actions the backend has not yet acknowledged — what the UI must keep showing. */
export async function listUnacknowledgedMutations(): Promise<QueuedMutation[]> {
  return (await listMutations()).filter((row) => row.status === 'pending' || row.status === 'syncing' || row.status === 'failed')
}

export async function updateMutation(id: string, patch: Partial<QueuedMutation>): Promise<void> {
  const existing = await withStore<QueuedMutation | undefined>('readonly', (store) => store.get(id) as IDBRequest<QueuedMutation | undefined>)
  if (!existing) return
  await withStore('readwrite', (store) => store.put({ ...existing, ...patch, updatedAt: new Date().toISOString() }) as IDBRequest<any>)
}

export async function removeMutation(id: string): Promise<void> {
  await withStore('readwrite', (store) => store.delete(id) as IDBRequest<any>)
}

/**
 * The backend rejects an offline punch captured outside this window (see
 * `createOfflinePunchSyncEvent`). A queued action older than this can never be
 * replayed, so retaining its precise coordinates has no operational value.
 */
export const OFFLINE_SYNC_WINDOW_MS = 14 * 24 * 60 * 60 * 1000

/** A queued action past the backend's window is permanently unreplayable. */
export function isBeyondSyncWindow(row: QueuedMutation, now = Date.now()): boolean {
  const capturedAt = new Date(row.capturedAt).getTime()
  if (!Number.isFinite(capturedAt)) return false
  return now - capturedAt > OFFLINE_SYNC_WINDOW_MS
}

/**
 * Bounded retention for the local queue.
 *
 * Two categories are removed, and NEITHER is a silent drop of work the worker
 * still needs:
 *  - Acknowledged rows (`synced` / `requires_review`), kept briefly so the
 *    worker can see the outcome of what they queued.
 *  - Rows that are already surfaced as `failed` AND are past the backend's
 *    replay window, so they were visible as failed for the full window and can
 *    never be submitted automatically again. Holding precise coordinates
 *    indefinitely on the device would serve no purpose.
 *
 * Pending and syncing rows are never pruned here at any age.
 */
export function selectPrunableMutations(
  rows: QueuedMutation[],
  retainMs = 24 * 60 * 60 * 1000,
  now = Date.now()
): QueuedMutation[] {
  const cutoff = now - retainMs
  return rows.filter((row) => {
    // Pending and syncing work is never pruned, at any age.
    const acknowledged = row.status === 'synced' || row.status === 'requires_review'
    const expiredAndSurfaced = row.status === 'failed' && isBeyondSyncWindow(row, now)
    return (acknowledged && new Date(row.updatedAt).getTime() < cutoff) || expiredAndSurfaced
  })
}

export async function pruneAcknowledgedMutations(retainMs = 24 * 60 * 60 * 1000, now = Date.now()): Promise<number> {
  const prunable = selectPrunableMutations(await listMutations(), retainMs, now)
  for (const row of prunable) await removeMutation(row.id)
  return prunable.length
}

export const PUNCH_SYNC_ACTIONS: Record<QueuedPunchAction, 'IN' | 'OUT' | 'BREAK_START' | 'BREAK_END'> = {
  'punch-in': 'IN',
  'punch-out': 'OUT',
  'break-start': 'BREAK_START',
  'break-end': 'BREAK_END',
}

/** Shape sent to the existing `/api/workers/me/punch-events/sync` contract. */
export function toPunchSyncEvent(row: QueuedMutation) {
  return {
    clientEventId: row.id,
    action: PUNCH_SYNC_ACTIONS[row.action as QueuedPunchAction],
    jobAssignmentId: row.assignmentId,
    deviceCapturedAt: row.capturedAt,
    lat: row.latitude as number,
    lon: row.longitude as number,
    accuracyMeters: row.accuracyMeters ?? null,
    offlineReason: 'browser_offline',
    networkType: row.deviceContext.networkType || null,
    deviceId: null,
    appVersion: row.deviceContext.appVersion || 'web',
    metadata: { source: 'web', displayMode: row.deviceContext.displayMode || null },
  }
}

/**
 * Only a punch captured WITH coordinates can be replayed offline — the sync
 * contract requires them, and inventing a location would corrupt evidence.
 */
export function isReplayablePunch(row: QueuedMutation): boolean {
  return row.kind === 'punch'
    && typeof row.latitude === 'number'
    && typeof row.longitude === 'number'
    && row.action in PUNCH_SYNC_ACTIONS
}
